import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { FilePenLine, Trash2 } from 'lucide-react'

import { ClickableCellWithTooltip } from '@/components/ClickableCellWithTooltip'
import { TableColumn } from '@/components/Table'
import { Alignment, FreezePosition } from '@/components/Table/tableHelper'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CompanyStatus, ResponseCompany } from '@/schema'
import { DATE_FORMAT } from '@/utils/constants'

export type CompanyAction = 'edit' | 'delete' | 'view-detail'

const getStatusClassName = (status: CompanyStatus): string => {
  switch (status) {
    case CompanyStatus.ACTIVE:
      return 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-50 text-success-700'
    case CompanyStatus.ARCHIVED:
      return 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700'
    default:
      return 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700'
  }
}

const getStatusLabel = (status: CompanyStatus, t: TFunction): string => {
  switch (status) {
    case CompanyStatus.ACTIVE:
      return t('company.status.active')
    case CompanyStatus.ARCHIVED:
      return t('company.status.archived')
    default:
      return status
  }
}

export const getCompanyListColumns = (
  handleActionClick: (action: CompanyAction, item: ResponseCompany) => void,
  t: TFunction
): TableColumn<ResponseCompany>[] => [
  {
    id: 'name',
    label: t('company.columns.name'),
    width: 240,
    sortable: true,
    renderCell: (value, row) => (
      <ClickableCellWithTooltip
        value={typeof value === 'string' ? (value?.toString() ?? '-') : '-'}
        onClick={() => handleActionClick('view-detail', row)}
      />
    ),
  },
  {
    id: 'status',
    label: t('company.columns.status'),
    width: 120,
    sortable: true,
    align: Alignment.LEFT,
    renderCell: (value, _row) => {
      const status = value as CompanyStatus
      return (
        <span className={getStatusClassName(status)}>
          {getStatusLabel(status, t)}
        </span>
      )
    },
  },
  {
    id: 'total_projects',
    label: t('company.columns.totalProjects'),
    width: 180,
    sortable: true,
    align: Alignment.RIGHT,
    alignLabel: Alignment.RIGHT,
  },
  {
    id: 'created_at',
    label: t('company.columns.createDate'),
    width: 180,
    sortable: true,
    align: Alignment.LEFT,
    renderCell: (value, _row) =>
      typeof value === 'string'
        ? dayjs(String(value)).format(DATE_FORMAT)
        : '-',
  },
  {
    id: 'actions',
    width: 90,
    isFrozen: true,
    freezePosition: FreezePosition.RIGHT,
    renderCell: (_value, row) => (
      <div className="flex gap-0.5 items-center">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleActionClick('edit', row)
              }}
            >
              <FilePenLine size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{t('company.actions.edit')}</span>
          </TooltipContent>
        </Tooltip>
        {row.total_projects === 0 && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  handleActionClick('delete', row)
                }}
              >
                <Trash2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('company.actions.delete')}</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    ),
  },
]
